import { defineSlotRecipe } from '@chakra-ui/react';
import { checkboxAnatomy } from '@chakra-ui/react/anatomy';

import {
  checkboxBorderRadius,
  checkboxBorderWidth,
  checkboxControlSize,
  checkboxHitTargetSize,
  checkboxIndicatorSize,
} from './checkbox.kds';

/** Styles for the visible checkbox box (lg control, or sm `[data-checkbox-control]`). */
const visualBoxBase = {
  bg: 'neutral.0',
  borderColor: 'brand.500',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

const checkedBox = {
  bg: 'brand.500',
  borderColor: 'brand.500',
  color: 'neutral.0',
} as const;

const indeterminateBox = {
  bg: 'neutral.0',
  borderColor: 'neutral.350',
  color: 'neutral.350',
} as const;

const disabledUncheckedBox = {
  opacity: 1,
  bg: 'neutral.0',
  borderColor: 'neutral.250',
} as const;

const disabledCheckedBox = {
  bg: 'neutral.250',
  borderColor: 'neutral.250',
  color: 'neutral.0',
} as const;

const disabledIndeterminateBox = {
  bg: 'neutral.0',
  borderColor: 'neutral.250',
  color: 'neutral.250',
} as const;

const uncheckedHover = { bg: 'brand.100' } as const;

/**
 * Chakra's default checkbox merges `checkmarkRecipe.variants.solid`, which paints
 * checked AND indeterminate with `colorPalette.solid` fill. Override here so KDS
 * per-state visuals (size axis) win.
 */
const solidVariant = {
  control: {
    borderRadius: checkboxBorderRadius,
    color: 'inherit',
    '&:is([data-state=checked], [data-state=indeterminate])': {
      bg: 'transparent',
      borderColor: 'transparent',
      color: 'inherit',
    },
  },
} as const;

/** Large size — state styles apply directly to the control. */
const lgVisualBoxStates = {
  '&[data-state=unchecked]:hover:not([data-disabled])': uncheckedHover,
  _checked: checkedBox,
  _indeterminate: indeterminateBox,
  _disabled: {
    ...disabledUncheckedBox,
    _checked: disabledCheckedBox,
    _indeterminate: disabledIndeterminateBox,
  },
} as const;

/** Small size — state styles target the inner `[data-checkbox-control]`. */
const smVisualBoxStates = {
  '&:hover:not([data-disabled])[data-state=unchecked] [data-checkbox-control]': uncheckedHover,
  '&[data-state=checked] [data-checkbox-control]': checkedBox,
  '&[data-state=indeterminate] [data-checkbox-control]': indeterminateBox,
  '&[data-disabled] [data-checkbox-control]': disabledUncheckedBox,
  '&[data-disabled][data-state=checked] [data-checkbox-control]': disabledCheckedBox,
  '&[data-disabled][data-state=indeterminate] [data-checkbox-control]': disabledIndeterminateBox,
} as const;

const checkboxSizeStyles = (size: keyof typeof checkboxControlSize) => ({
  root: {
    gap: size === 'lg' ? '8px' : '4px',
  },
  label: size === 'lg' ? { textStyle: 'large.regular' } : { textStyle: 'default.regular' },
  indicator: {
    boxSize: checkboxIndicatorSize[size],
    '& svg': {
      boxSize: 'full',
    },
  },
  ...(size === 'sm'
    ? {
        control: {
          boxSize: checkboxHitTargetSize.sm,
          border: 'none',
          bg: 'transparent',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          '& [data-checkbox-control]': {
            ...visualBoxBase,
            boxSize: checkboxControlSize.sm,
            borderWidth: checkboxBorderWidth.sm,
            borderRadius: checkboxBorderRadius,
          },
          ...smVisualBoxStates,
        },
      }
    : {
        control: {
          ...visualBoxBase,
          boxSize: checkboxControlSize.lg,
          borderWidth: checkboxBorderWidth.lg,
          borderRadius: checkboxBorderRadius,
          ...lgVisualBoxStates,
        },
      }),
});

/** KDS Checkbox — default, hover, checked, disabled, indeterminate. */
export const checkboxSlotRecipe = defineSlotRecipe({
  slots: checkboxAnatomy.keys(),
  base: {
    control: {
      // Chakra checkmark defaults disabled control to opacity 0.5.
      _disabled: {
        opacity: 1,
      },
    },
    root: {
      '&[data-state=indeterminate] [data-part=label], &[data-state=indeterminate] .chakra-checkbox__label': {
        color: 'neutral.350',
      },
      _hover: {
        '&:not([data-disabled])[data-state=unchecked] [data-part=label], &:not([data-disabled])[data-state=unchecked] .chakra-checkbox__label':
          {
            color: 'neutral.500',
          },
      },
    },
    label: {
      fontWeight: 'normal',
      color: 'neutral.400',
      _disabled: {
        color: 'neutral.350',
        opacity: 0.8,
      },
    },
  },
  variants: {
    variant: {
      solid: solidVariant,
    },
    size: {
      sm: checkboxSizeStyles('sm'),
      lg: checkboxSizeStyles('lg'),
    },
  },
  defaultVariants: {
    variant: 'solid',
    size: 'sm',
  },
});
